/* MoodAtmosphere — five fixed full-viewport light layers, one per section
   mood (hero / work / latently / how / closing). A single IntersectionObserver
   watches every element carrying data-mood and flips document.documentElement's
   data-mood attribute to whichever section is centred in the viewport; CSS
   then crossfades the matching layer to opacity 1. Mounted once in App.tsx,
   above Nav, so the field sits under everything the page renders.

   z-order this depends on (documented per the mood-atmosphere task contract):
   paper (bg) < body::before base atmo (-3) < these mood layers (-2) < grain
   body::after (-1) < content (auto/static). css-foundation owns body::before
   and body::after; this file only ever sets its own layers to -2. */

import { useEffect, useRef } from 'react';
import './MoodAtmosphere.css';

const MOODS = ['hero', 'work', 'latently', 'how', 'closing'] as const;

export function MoodAtmosphere(): JSX.Element {
  const rootRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    // Reduced motion still switches moods (the atmosphere is informational,
    // not decorative) but skips the crossfade — a class on <html> lets CSS
    // drop the transition without a second copy of every rule.
    const reduceQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const applyReduceClass = () => {
      document.documentElement.classList.toggle('mood-instant', reduceQuery.matches);
    };
    applyReduceClass();
    reduceQuery.addEventListener('change', applyReduceClass);

    // Mobile gets a faster crossfade (700ms vs 900ms); toggled as a class
    // rather than duplicating the transition rule at a breakpoint, per the
    // contract's "via a matchMedia check that toggles a class" instruction.
    const narrowQuery = window.matchMedia('(max-width: 640px)');
    const applyNarrowClass = () => {
      document.documentElement.classList.toggle('mood-narrow', narrowQuery.matches);
    };
    applyNarrowClass();
    narrowQuery.addEventListener('change', applyNarrowClass);

    document.documentElement.dataset.mood = 'hero';

    const targets = Array.from(document.querySelectorAll<HTMLElement>('[data-mood]'));
    const observer = new IntersectionObserver(
      (entries) => {
        // Multiple sections can intersect the thin centre band at once during
        // a fast scroll; pick the one with the most overlap so the mood never
        // flickers between two candidates.
        let best: { mood: string; ratio: number } | null = null;
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          const mood = entry.target.getAttribute('data-mood');
          if (!mood) continue;
          if (!best || entry.intersectionRatio > best.ratio) {
            best = { mood, ratio: entry.intersectionRatio };
          }
        }
        if (best) {
          document.documentElement.dataset.mood = best.mood;
        }
      },
      { rootMargin: '-45% 0px -45% 0px', threshold: [0, 0.25, 0.5, 0.75, 1] }
    );

    for (const target of targets) observer.observe(target);

    return () => {
      observer.disconnect();
      reduceQuery.removeEventListener('change', applyReduceClass);
      narrowQuery.removeEventListener('change', applyNarrowClass);
    };
  }, []);

  return (
    <div className="mood" aria-hidden="true" ref={rootRef}>
      {MOODS.map((mood) => (
        <i key={mood} data-mood-layer={mood} />
      ))}
    </div>
  );
}
